import React from 'react';
import { useCampo } from './Field';

/**
 * Campo de texto do design system (at-58, frente C).
 *
 * 🔴 **`data-ds-campo` NÃO é decoração — é metade de um par.** A outra metade é
 * a regra `input[type="text"]:not([data-ds-campo])` em
 * `ui/tokens/legado-compartilhado.css`. Sem o marcador aqui, o seletor legado
 * **vence** este componente: ele é `input[type="text"]` — especificidade (0,1,1)
 * — contra (0,1,0) de qualquer utilitário do Tailwind, e as utilities saem de
 * `ui/tokens/base.css`, o arquivo #1 do bundle. O campo sairia com raio de 4px,
 * borda #ccc e AmpleSoft, **sem erro nenhum**. O `SearchInput` (at-56) escapou
 * por outro caminho (`type="search"`), que não serve a um campo de nome.
 *
 * ⚠️ **Mexer num sem o outro quebra os dois lados**, e o modo de falha é
 * silencioso — há teste em `ui.test.tsx` afirmando que o marcador está presente.
 *
 * ⚠️ **Teclado certo por campo** (spec 03 §1): quem chama passa `inputMode`
 * (`numeric` para CPF, CEP, telefone). No celular isso decide se o consultor
 * digita número num teclado de número ou caça dígitos no de letras.
 */

interface PropsInput extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Estado de erro. Normalmente vem do `Field` pelo contexto — passar aqui é só
   * para uso solto, fora de um `Field`.
   */
  erro?: boolean;
}

/** Moldura compartilhada com `Select` e com o botão da regional. */
export function molduraCampo(temErro: boolean): string {
  return [
    /* Medidas do protótipo (`rounded-xl`, `px-4 py-3`). `py-3` + `text-sm` dá
       44px de altura — o alvo de toque mínimo da spec 01 §7. */
    'tw-w-full tw-appearance-none tw-rounded-xl tw-border tw-bg-white',
    'tw-px-4 tw-py-3 tw-font-corpo tw-text-sm tw-text-texto-titulo',
    'placeholder:tw-text-texto-desabilitado',
    'tw-transition-colors focus:tw-outline-none focus:tw-ring-2',
    'disabled:tw-cursor-not-allowed disabled:tw-bg-superficie disabled:tw-text-texto-desabilitado',
    temErro
      ? 'tw-border-danger focus:tw-border-danger focus:tw-ring-danger/20'
      : 'tw-border-superficie-borda focus:tw-border-brand focus:tw-ring-brand/20',
  ].join(' ');
}

const Input: React.FC<PropsInput> = ({ erro, className = '', ...resto }) => {
  const campo = useCampo();
  const temErro = erro ?? campo?.temErro ?? false;

  return (
    <input
      /* 🔴 O marcador. Ver o aviso no topo do arquivo. */
      data-ds-campo=""
      id={campo?.id}
      aria-invalid={temErro || undefined}
      aria-describedby={campo?.descricao}
      className={[molduraCampo(temErro), className].filter(Boolean).join(' ')}
      {...resto}
    />
  );
};

export default Input;
